// src/pages/PageListPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import { getPages, deletePage, getPagesCount, createPage } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { getSubdomainByAccountId } from '../services/subdomainService';
import { formatDateTimeToMinute } from '../utils/dateUtils';
import '../styles/pages/PageListPage.scss';

// 環境に応じたベースURLを取得
const getBaseUrl = () => {
    // 本番環境の場合は実際のドメインを使用
    if (window.location.hostname !== 'localhost') {
        return `${window.location.protocol}//${window.location.hostname}`;
    }
    // 開発環境の場合は設定可能なポートを使用
    return 'http://localhost:5174';
};

const PUBLIC_SITE_BASE_URL = getBaseUrl();
const PAGE_SIZE = 10;

const PageListPage = () => {
    const [pages, setPages] = useState([]);
    const [rawPages, setRawPages] = useState([]); // 元のページデータを保持
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalFetched, setTotalFetched] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [userSubdomain, setUserSubdomain] = useState(null); // ユーザーのサブドメイン登録情報
    const { currentUser, accountId, loading: authLoading } = useAuth();
    const { tenant } = useTenant();
    const navigate = useNavigate();

    // ユーザーのサブドメイン登録情報を取得
    useEffect(() => {
        const fetchUserSubdomain = async () => {
            if (currentUser && accountId) {
                try {
                    const subdomainData = await getSubdomainByAccountId(accountId);
                    setUserSubdomain(subdomainData);
                } catch (error) {
                    console.error('サブドメイン情報の取得に失敗しました:', error);
                    setUserSubdomain(null);
                }
            }
        };

        fetchUserSubdomain();
    }, [currentUser, accountId]);

    const fetchPages = useCallback(async (isLoadMore = false) => {
        if (!currentUser) {
            return;
        }


        if (!isLoadMore) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);

        try {
            // 全データ数を取得
            const totalCount = await getPagesCount(currentUser.uid);
            setTotalPages(totalCount);


            const result = await getPages(currentUser.uid, {
                pageSize: PAGE_SIZE,
                lastDoc: isLoadMore ? lastVisible : null
            });



            // 元のページデータを保存（個ページ判定用）
            if (isLoadMore) {
                setRawPages(prev => [...prev, ...result.pages]);
            } else {
                setRawPages(result.pages);
            }

            // ページを階層構造に整理する
            const hierarchicalPages = buildHierarchy(result.pages);



            if (isLoadMore) {
                // 重複を防ぐために、既存のページIDをチェック
                setPages(prevPages => {
                    const existingIds = new Set(prevPages.map(p => p.id));
                    const newPages = hierarchicalPages.filter(page => !existingIds.has(page.id));
                    return [...prevPages, ...newPages];
                });
                setCurrentPage(prev => prev + 1);
                setTotalFetched(prev => prev + result.pages.length);
            } else {
                setPages(hierarchicalPages);
                setCurrentPage(1);
                setTotalFetched(result.pages.length);
            }

            setLastVisible(result.lastVisible);

            // hasMoreの判定を修正
            // result.lastVisibleがnullの場合、または取得したページ数がPAGE_SIZEより少ない場合は最後のページ
            const isLastPage = !result.lastVisible || result.pages.length < PAGE_SIZE;
            setHasMore(!isLastPage);


        } catch (err) {
            console.error('ページの取得に失敗しました:', err);
            setError('ページの読み込み中にエラーが発生しました。');
        } finally {
            if (!isLoadMore) {
                setLoading(false);
            } else {
                setLoadingMore(false);
            }
        }
    }, [currentUser, lastVisible]);

    useEffect(() => {
        if (authLoading || !currentUser) {
            if (!currentUser && !authLoading) {
                navigate('/login');
            }
            return;
        }
        setLastVisible(null); // ページ取得時にリセット
        setHasMore(true);
        setCurrentPage(1); // ページ番号もリセット
        setTotalFetched(0); // 取得総数もリセット
        setTotalPages(0); // 全データ数もリセット
        setRawPages([]); // 元のページデータもリセット
        fetchPages();
    }, [currentUser, authLoading, navigate]);

    const handleEdit = (pageId) => {
        navigate(`/pages/${accountId}/edit/${pageId}`);
    };

    const handleDuplicate = async (pageId, title) => {
        if (window.confirm(`ページ「${title}」を複製しますか？\n複製されたページは下書き状態で作成されます。`)) {
            setLoading(true);
            try {
                // 元のページデータを取得
                const originalPage = rawPages.find(p => p.id === pageId);
                if (!originalPage) {
                    throw new Error('元のページが見つかりません。');
                }

                // 複製用のデータを作成
                const duplicatedPage = {
                    title: `${originalPage.title} (コピー)`,
                    slug: originalPage.slug, // 初期値は元のスラッグ
                    content: originalPage.content || '',
                    head: originalPage.head || '',
                    body: originalPage.body || '',
                    css: originalPage.css || '',
                    status: 'draft', // 複製時は下書き状態
                    jsonld: originalPage.jsonld || '',
                    snsImage: originalPage.snsImage || '',
                    parentId: originalPage.parentId || null,
                    authorId: currentUser.uid,
                    accountId: currentUser.uid,
                };

                // 同じ親ページ配下でのスラッグの重複チェック
                const targetParentId = duplicatedPage.parentId;
                const existingSlugs = rawPages
                    .filter(p => {
                        // 同じ親ページ配下のページのみを対象とする
                        const pageParentId = p.parentId === '' ? null : p.parentId;
                        const targetParent = targetParentId === '' ? null : targetParentId;
                        return pageParentId === targetParent;
                    })
                    .map(p => p.slug);

                // 重複しないスラッグを生成
                let counter = 1;
                let newSlug = duplicatedPage.slug;

                // 元のスラッグが既に存在する場合、または「-copy」で終わるスラッグが存在する場合
                while (existingSlugs.includes(newSlug)) {
                    if (counter === 1) {
                        newSlug = `${duplicatedPage.slug}-copy`;
                    } else {
                        newSlug = `${duplicatedPage.slug}-copy-${counter}`;
                    }
                    counter++;
                }
                duplicatedPage.slug = newSlug;

                // ページを作成
                await createPage(duplicatedPage, currentUser.uid);

                alert('ページが複製されました！');
                await fetchPages(); // ページ一覧を更新
            } catch (err) {
                console.error('ページの複製に失敗しました:', err);
                setError('ページの複製中にエラーが発生しました。');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDelete = async (pageId, title) => {
        if (window.confirm(`ページ「${title}」を本当に削除しますか？\n（このページの子ページも削除されます）`)) {
            setLoading(true);
            try {
                const result = await getPages(currentUser.uid);
                const allFetchedPages = result.pages;
                if (!Array.isArray(allFetchedPages)) { // ここでも念のためチェック
                    throw new Error('削除時のページデータ取得に失敗しました。');
                }

                const pagesToDelete = [pageId];
                const findChildren = (parentId) => {
                    allFetchedPages.forEach(p => {
                        if (p.parentId === parentId) {
                            pagesToDelete.push(p.id);
                            findChildren(p.id);
                        }
                    });
                };
                findChildren(pageId);

                // 削除対象のページを取得
                const pagesToDeleteData = allFetchedPages.filter(p => pagesToDelete.includes(p.id));

                // 公開中、非公開、下書きのページが含まれているかチェック
                const nonArchivedPages = pagesToDeleteData.filter(p => p.status !== 'archived');
                if (nonArchivedPages.length > 0) {
                    const publishedPages = nonArchivedPages.filter(p => p.status === 'published');
                    const unpublishedPages = nonArchivedPages.filter(p => p.status === 'unpublished');
                    const draftPages = nonArchivedPages.filter(p => p.status === 'draft');

                    let errorMessage = '削除できません。以下のページが含まれています：\n\n';

                    if (publishedPages.length > 0) {
                        const publishedPageTitles = publishedPages.map(p => p.title).join('\n• ');
                        errorMessage += `【公開中】\n• ${publishedPageTitles}\n\n`;
                    }

                    if (unpublishedPages.length > 0) {
                        const unpublishedPageTitles = unpublishedPages.map(p => p.title).join('\n• ');
                        errorMessage += `【非公開】\n• ${unpublishedPageTitles}\n\n`;
                    }

                    if (draftPages.length > 0) {
                        const draftPageTitles = draftPages.map(p => p.title).join('\n• ');
                        errorMessage += `【下書き】\n• ${draftPageTitles}\n\n`;
                    }

                    errorMessage += '公開中、非公開、下書きのページは削除できません。先にアーカイブにしてください。\n\n※ アーカイブページに小ページがある場合でも、その小ページにアーカイブ以外のステータスのページが含まれている場合は削除できません。';

                    alert(errorMessage);
                    setLoading(false);
                    return;
                }

                // 削除対象のページの詳細を表示
                const deleteConfirmMessage = pagesToDeleteData.map(p => {
                    const statusText = p.status === 'draft' ? '下書き' :
                        p.status === 'published' ? '公開中' :
                            p.status === 'unpublished' ? '非公開' : 'アーカイブ';

                    // 階層レベルに応じてインデントを追加
                    const indent = '  '.repeat(p.level || 0);
                    return `${indent}• ${p.title} (${statusText})`;
                }).join('\n');

                const finalConfirm = window.confirm(
                    `以下のページを削除しますか？\n\n${deleteConfirmMessage}\n\nこの操作は取り消せません。`
                );

                if (!finalConfirm) {
                    setLoading(false);
                    return;
                }

                // ページとHTMLファイルを削除
                for (const idToDelete of pagesToDelete) {
                    // 削除対象ページの情報を取得
                    const pageToDelete = pagesToDeleteData.find(p => p.id === idToDelete);
                    
                    // HTMLファイルを削除（slug が存在する場合のみ）
                    if (pageToDelete && pageToDelete.slug) {
                        try {
                            const { deleteStaticPageHTML } = await import('../services/staticHostingService');
                            
                            // メインサイト用HTMLファイルを削除
                            await deleteStaticPageHTML(pageToDelete.slug);
                            console.log(`✅ ページ削除: メインサイト用HTMLファイル削除完了 (${pageToDelete.slug})`);
                            
                            // サブドメイン用HTMLファイルを削除
                            if (userSubdomain && userSubdomain.subdomain) {
                                await deleteStaticPageHTML(pageToDelete.slug, userSubdomain.subdomain);
                                console.log(`✅ ページ削除: サブドメイン用HTMLファイル削除完了 (${pageToDelete.slug})`);
                            }
                        } catch (deleteHtmlError) {
                            console.warn(`⚠️ HTMLファイル削除エラー (${pageToDelete.slug}):`, deleteHtmlError);
                            // HTMLファイル削除エラーは処理を停止させない
                        }
                    }
                    
                    // Firestoreからページを削除
                    await deletePage(idToDelete);
                }

                await fetchPages();
            } catch (err) {
                console.error('ページの削除に失敗しました:', err);
                setError('ページの削除中にエラーが発生しました。');
            } finally {
                setLoading(false);
            }
        }
    };

    // サブドメイン付き公開URL生成
    const getPublicUrl = (slug) => {
        if (!slug) return 'N/A';

        if (userSubdomain && userSubdomain.subdomain && userSubdomain.subdomain !== 'default') {
            // ローカル環境
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                // indexページの場合はルートURL
                if (slug === 'index') {
                    return `http://${userSubdomain.subdomain}.localhost:5174/`;
                }
                return `http://${userSubdomain.subdomain}.localhost:5174/${slug}`;
            }
            // 本番環境
            if (slug === 'index') {
                return `https://${userSubdomain.subdomain}.suisui.life/`;
            }
            return `https://${userSubdomain.subdomain}.suisui.life/${slug}`;
        }

        // サブドメイン未設定時は従来のURL
        if (slug === 'index') {
            return `${PUBLIC_SITE_BASE_URL}/`;
        }
        return `${PUBLIC_SITE_BASE_URL}/${slug}`;
    };

    const getPreviewUrl = (pageId) => {
        const fullUrl = `${PUBLIC_SITE_BASE_URL}/preview/${accountId}/${pageId}`;
        return fullUrl;
    };

    // ページを階層構造に変換するヘルパー関数
    const buildHierarchy = (flatPages) => {
        // 念のため、ここでも引数が配列であることを確認
        if (!Array.isArray(flatPages)) {
            console.error('buildHierarchy に渡されたデータが配列ではありません:', flatPages);
            return []; // 安全のため空の配列を返す
        }

        // 重複を除去（IDでユニークにする）
        const uniquePages = flatPages.filter((page, index, self) =>
            index === self.findIndex(p => p.id === page.id)
        );

        const pageMap = new Map(uniquePages.map(p => [p.id, { ...p, children: [] }]));
        const rootPages = [];

        pageMap.forEach(page => {
            if (page.parentId && pageMap.has(page.parentId)) {
                pageMap.get(page.parentId).children.push(page);
            } else {
                rootPages.push(page);
            }
        });

        const sortHierarchy = (pagesArray) => {
            pagesArray.sort((a, b) => a.title.localeCompare(b.title));
            pagesArray.forEach(p => {
                if (p.children.length > 0) {
                    sortHierarchy(p.children);
                }
            });
        };
        sortHierarchy(rootPages);

        const flattenHierarchy = (pagesArray, level = 0) => {
            let result = [];
            pagesArray.forEach(page => {
                result.push({ ...page, level: level });
                if (page.children.length > 0) {
                    result = result.concat(flattenHierarchy(page.children, level + 1));
                }
            });
            return result;
        };

        return flattenHierarchy(rootPages);
    };

    // 個ページが存在するかどうかを判定する関数
    const hasChildPages = (pageId) => {
        // 元のページデータから判定
        const hasChildren = rawPages.some(page => page.parentId === pageId);
        return hasChildren;
    };

    // アーカイブページで小ページにアーカイブ以外のステータスが存在するかどうかを判定する関数
    const hasNonArchivedChildPages = (pageId) => {
        const hasNonArchivedChildren = rawPages.some(page =>
            page.parentId === pageId && page.status !== 'archived'
        );
        return hasNonArchivedChildren;
    };

    // 削除対象に公開中のページが含まれるかどうかを判定する関数
    const hasPublishedPagesInDeleteTarget = (pageId) => {
        const pagesToCheck = [pageId];

        // 子ページのIDを再帰的に取得
        const findChildren = (parentId) => {
            rawPages.forEach(p => {
                if (p.parentId === parentId) {
                    pagesToCheck.push(p.id);
                    findChildren(p.id);
                }
            });
        };
        findChildren(pageId);

        // 削除対象に公開中のページが含まれているかチェック
        const hasPublished = rawPages.some(page =>
            pagesToCheck.includes(page.id) && page.status === 'published'
        );

        return hasPublished;
    };

    // フィルタリングされたページを取得する関数
    const getFilteredPages = () => {
        // すべてのページを表示（アーカイブページも含む）
        return pages;
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div>
                <Header />
                <main className="page-list-container">
                    <p className="error-message">{error}</p>
                </main>
            </div>
        );
    }

    const filteredPages = getFilteredPages();

    return (
        <div>
            <Header />
            <main className="page-list-container">
                <div className="page-list-header">
                    <button onClick={() => navigate(`/pages/${accountId}/new`)} className="create-page-button">
                        新規ページ作成
                    </button>
                </div>

                {filteredPages.length === 0 ? (
                    <p>まだページがありません。新しいページを作成しましょう！</p>
                ) : (
                    <>
                        <table className="pages-table">
                            <thead>
                                <tr>
                                    <th>タイトル</th>
                                    <th>スラッグ</th>
                                    <th>ファイル種類</th>
                                    <th>ファイル名</th>
                                    <th className='status-columns'>公開</th>
                                    <th>URL</th>
                                    <th className='operation-column'>操作</th>
                                    <th className="time-column">作成日時</th>
                                    <th className="time-column">最終更新日時</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPages.map((page) => (
                                    <tr key={page.id} className={page.status === 'archived' ? 'archived-row' : ''}>
                                        <td className="page-title">
                                            {page.level > 0 && <span style={{ marginRight: '5px' }}>└</span>}
                                            {page.slug === 'index' && (
                                                <span className="root-page-badge" title="ルートページ（index）">
                                                    🏠
                                                </span>
                                            )}
                                            {page.title}
                                            {page.status === 'archived' && (
                                                <span className="archived-badge" title="アーカイブ済み">
                                                    📁
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {page.slug === 'index' ? (
                                                <span className="root-slug">/</span>
                                            ) : (
                                                page.slug
                                            )}
                                        </td>
                                        <td className="file-type">
                                            <span className={`file-type-badge file-type-${page.fileType || 'html'}`}>
                                                {page.fileType === 'html' ? 'HTML' :
                                                    page.fileType === 'css' ? 'CSS' :
                                                        page.fileType === 'js' ? 'JS' : 'HTML'}
                                            </span>
                                        </td>
                                        <td className="file-name">
                                            {page.fileName || page.slug}
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${page.status}`}>
                                                {page.status === 'draft' ? '下書き' :
                                                    page.status === 'published' ? '公開中' :
                                                        page.status === 'unpublished' ? '非公開' :
                                                            'アーカイブ'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="url-links">
                                                {page.status === 'published' && (
                                                    <div className="url-item">
                                                        <a
                                                            href={getPublicUrl(page.slug)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="public-url-link"
                                                        >
                                                            {getPublicUrl(page.slug)}
                                                        </a>
                                                    </div>
                                                )}
                                                <div className="url-item">
                                                    <a
                                                        href={getPreviewUrl(page.id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="preview-url-link"
                                                    >
                                                        プレビュー
                                                    </a>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleEdit(page.id)}
                                                className="edit-button"
                                                disabled={page.status === 'archived' && hasNonArchivedChildPages(page.id)}
                                                title={page.status === 'archived' && hasNonArchivedChildPages(page.id) ? '小ページにアーカイブ以外のステータスが存在するため、編集できません。' : ''}
                                            >
                                                編集
                                            </button>
                                            <button
                                                onClick={() => handleDuplicate(page.id, page.title)}
                                                className="duplicate-button"
                                                title="ページを複製"
                                            >
                                                複製
                                            </button>
                                            <button
                                                onClick={() => handleDelete(page.id, page.title)}
                                                className="delete-button"
                                                title="ページを削除"
                                            >
                                                削除
                                            </button>
                                        </td>
                                        <td>{formatDateTimeToMinute(page.createdAt)}</td>
                                        <td>{formatDateTimeToMinute(page.updatedAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {hasMore && (
                            <div className="load-more-container">
                                <div className="pagination-info">
                                    <p>現在 {currentPage} ページ目を表示中</p>
                                    <p>表示件数: {filteredPages.length}件</p>
                                    <p>取得総数: {totalFetched}件 / 全{totalPages}件</p>
                                </div>
                                <button
                                    onClick={() => fetchPages(true)}
                                    disabled={loadingMore}
                                    className="load-more-button"
                                >
                                    {loadingMore ? '読み込み中...' : 'もっと見る'}
                                </button>
                            </div>
                        )}
                        {!hasMore && filteredPages.length > 0 && (
                            <div className="load-more-container">
                                <div className="pagination-info">
                                    <p>全てのページを表示しました</p>
                                    <p>表示件数: {filteredPages.length}件</p>
                                    <p>取得総数: {totalFetched}件 / 全{totalPages}件</p>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div >
    );
};

export default PageListPage;